import { BBox, CRS_EPSG4326, S2L1CLayer } from '@sentinel-hub/sentinelhub-js';
import toBBox from '@turf/bbox';
import dayjs from 'dayjs';
import { useEffect, useRef, useState, useContext } from 'react';
import { AiFillCalendar } from 'react-icons/ai';
import { BsFillPlayFill } from 'react-icons/bs';
import { FaTractor } from 'react-icons/fa';
import { HiOutlineChevronLeft } from 'react-icons/hi';
import { MdClose } from 'react-icons/md';
import { useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { t } from 'ttag';

import useSearchParamsWrapper from '../../hooks/useSearchParamsWrapper';
import Spinner from '../../media/spinner.svg';
import { FETCH_STATUSES } from '../Fields/constants/consts';
import ObservationComponent from '../Shared/AreaMonitoring/ObservationComponent';
import SignalChartComponent from '../Shared/AreaMonitoring/SignalChartComponent';
import TimelapseComponent from '../Shared/AreaMonitoring/TimelapseComponent';
import { ButtonPrimarySmall, ButtonWhiteMedium } from '../Shared/Button/Button';
import ErrorHandler from '../Shared/ErrorHandler/ErrorHandler';
import FieldLabel from '../Shared/FieldLabel/FieldLabel';
import Label from '../Shared/Label/Label';
import Switch from '../Shared/Switch/Switch';
import { PERFORMANCE_INDEX_LAYERS, TRUE_COLOR_BASE_LAYER } from './constants/performance-index-layers';
import { FieldPanelRight, FieldStatusWrap, FieldTimelapse, FieldWrap, ObservationCard } from './Field.styles';
import FieldMap from './FieldMap';
import PerformanceIndexDescription from './PerformanceIndexDescription';
import { PanelLeft } from '../Shared/PanelLeft/PanelLeft';
import { LanguageContext } from '../../contexts/LanguageContext';
import api from '../../api/api';
import useCropTypes, { CROP_TYPE_CODES } from '../../hooks/useCropTypes';
import { parseFullYearFromScope, parseScopeFromId, parseCodeFromId } from '../../utils/utils';
import Filter from '../Shared/Filter/Filter';
import useFilters from '../Shared/Filter/useFilters';
import { FILTERS } from './constants/filters';
import RadioButton from '../Shared/RadioButton/RadioButton';
import useSoilTypes, { SOIL_TYPE_CODES } from '../../hooks/useSoilTypes';

export default function FieldContainer() {
  const { getSearchParams } = useSearchParamsWrapper();
  const [language] = useContext(LanguageContext);

  const [field, setField] = useState();
  const [fieldStatus, setFieldStatus] = useState(FETCH_STATUSES.loading);
  const fieldAbortController = useRef(new AbortController());

  const [neighbouringFields, setNeighbouringFields] = useState([]);
  const [neighbouringFieldsStatus, setNeighbouringFieldsStatus] = useState(FETCH_STATUSES.loading);
  const neighbouringFieldsAbortController = useRef();

  const [availableDates, setAvailableDates] = useState([]);
  const [availableDatesStatus, setAvailableDatesStatus] = useState(FETCH_STATUSES.loading);

  const [hasSelectedPerformanceIndex, setHasSelectedPerformanceIndex] = useState(false);
  const [performanceIndex, setPerformanceIndex] = useState(TRUE_COLOR_BASE_LAYER.id);

  const [shouldDisplayTimelapse, setShouldDisplayTimelapse] = useState(false);
  const [isAnalysingMultipleFields, setIsAnalysingMultipleFields] = useState(false);

  const { fieldId } = useParams();
  const { search } = useLocation();

  const [date, setDate] = useState(
    () => new Date(`${parseFullYearFromScope(parseScopeFromId(fieldId))}-01-01`),
  );

  const { getCropNameById } = useCropTypes(getSearchParams().year);

  const FIELD_SCOPE = parseScopeFromId(fieldId);

  useEffect(() => {
    getField();
    return () => {
      //eslint-disable-next-line
      fieldAbortController.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (neighbouringFieldsAbortController.current) {
      neighbouringFieldsAbortController.current.abort();
    }
    neighbouringFieldsAbortController.current = new AbortController();
    if (isAnalysingMultipleFields) {
      getNeighbouringFields();
    } else {
      setNeighbouringFields([]);
    }
    return () => {
      neighbouringFieldsAbortController.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalysingMultipleFields]);

  useEffect(() => {
    if (field) {
      fetchCloudFreeDates();
      setUnsavedAndSavedFilter(FILTERS.cropType, parseCodeFromId(field.cropId));
      setUnsavedAndSavedFilter(FILTERS.soilType, parseCodeFromId(field.soilTypeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field]);

  async function getNeighbouringFields() {
    const filters = savedFilters;

    setNeighbouringFieldsStatus(FETCH_STATUSES.loading);
    const year = parseScopeFromId(field.cropId);
    const maxAmountOfNeighbours = 100;
    const maxNeighbourDistanceInMeters = 10000;

    const queryArguments = [`size=${maxAmountOfNeighbours}`, `distance=${maxNeighbourDistanceInMeters}`];

    if (filters) {
      if (filters.cropType !== CROP_TYPE_CODES.ALL) {
        const cropId = `${filters.year}.CL_CROP.${filters.cropType}`;
        queryArguments.push(`cropId=${cropId}`);
      }
      if (filters.soilType !== SOIL_TYPE_CODES.ALL) {
        const soilTypeId = `${filters.year}.CL_ST.${filters.soilType}`;
        queryArguments.push(`soilTypeId=${soilTypeId}`);
      }
    } else {
      queryArguments.push(`cropId=${field.cropId}`);
      queryArguments.push(`soilTypeId=${field.soilTypeId}`);
    }

    const { signal } = neighbouringFieldsAbortController.current;

    try {
      const neighbouringFields = await api().fetchNeighbouringFields(fieldId, year, queryArguments, signal);

      const selectedFields = neighbouringFields.map((field, index) => {
        return {
          ...field,
          isSelected: index < 2,
        };
      });

      setNeighbouringFields(selectedFields);
      setNeighbouringFieldsStatus(FETCH_STATUSES.success);
    } catch (error) {
      if (error.message !== 'canceled') {
        setNeighbouringFieldsStatus(FETCH_STATUSES.error);
      }
    }
  }

  async function fetchCloudFreeDates() {
    setAvailableDates(FETCH_STATUSES.loading);
    const bbox = toBBox(field.geometry);

    const sentinelHubBBox = new BBox(CRS_EPSG4326, bbox[0], bbox[1], bbox[2], bbox[3]);
    const layer = new S2L1CLayer({
      instanceId: process.env.REACT_APP_SENTINEL_HUB_CLIENT_ID,
      layerId: performanceIndex,
      maxCloudCoverPercent: 0,
    });
    const yearAsYYYY = parseFullYearFromScope(parseScopeFromId(field.cropId));
    const MIN_DATE = new Date(`${yearAsYYYY}-01-01`);
    const MAX_DATE = new Date(`${yearAsYYYY}-12-31`);
    try {
      const availableDates = await layer.findFlyovers(sentinelHubBBox, MIN_DATE, MAX_DATE);
      setAvailableDates(availableDates);
      setDate(availableDates[0].fromTime);
      setAvailableDatesStatus(FETCH_STATUSES.success);
    } catch (error) {
      setAvailableDatesStatus(FETCH_STATUSES.error);
    }
  }
  async function getField() {
    setFieldStatus(FETCH_STATUSES.loading);
    const { signal } = fieldAbortController.current;
    try {
      const field = await api().fetchField(fieldId, signal);
      setField(field);
      setFieldStatus(FETCH_STATUSES.success);
    } catch (error) {
      if (error.message !== 'canceled') {
        setFieldStatus(FETCH_STATUSES.error);
      }
    }
  }

  const {
    handleFilterChange,
    handleFilterToggleClick,
    handleFilterCloseClick,
    handleFilterSaveClick,
    setUnsavedAndSavedFilter,
    activeFilter,
    savedFilters,
    unsavedFilters,
  } = useFilters(getFilters());

  useEffect(() => {
    if (neighbouringFieldsAbortController.current) {
      neighbouringFieldsAbortController.current.abort();
    }
    neighbouringFieldsAbortController.current = new AbortController();

    if (isAnalysingMultipleFields) {
      getNeighbouringFields();
    }

    return () => {
      neighbouringFieldsAbortController.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilters]);

  const { soilTypes } = useSoilTypes(savedFilters.year);
  const { cropTypes } = useCropTypes(savedFilters.year);

  function getFilters() {
    const { cropType, year, mapLayer, soilType } = getSearchParams();
    return {
      cropType,
      year,
      mapLayer,
      soilType,
    };
  }

  if (fieldStatus === FETCH_STATUSES.loading) {
    return (
      <FieldStatusWrap>
        <img src={Spinner} alt="" />
      </FieldStatusWrap>
    );
  }

  if (fieldStatus === FETCH_STATUSES.error) {
    return (
      <FieldStatusWrap>
        <ErrorHandler
          title={t`Error`}
          description={t`Something went wrong when fetching field`}
          onTryAgainClick={getField}
        />
      </FieldStatusWrap>
    );
  }

  function renderSelectPerformanceIndex() {
    return (
      <div style={{ height: '100%' }}>
        <div>
          <Link to={`/${search}`} className="button-select-new-field">
            <HiOutlineChevronLeft />
            {t`Select new field`}
          </Link>
        </div>
        <div className="field-parameters">
          <div>
            <Label>{t`I want to analyse`}</Label>
            <ul className="field-performance-index-selector-list">
              {PERFORMANCE_INDEX_LAYERS.map((layer, index) => (
                <li
                  onClick={() => {
                    setHasSelectedPerformanceIndex(true);
                    setPerformanceIndex(layer.id);
                  }}
                  tabIndex={index}
                  className="field-performance-index-selector-item"
                  key={index}
                >
                  <div
                    className="field-performance-index-selector-background"
                    style={{
                      background: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${layer.previewImage})`,
                    }}
                  >
                    {layer.name()}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  function renderAnalysePerformanceIndex() {
    return (
      <div>
        <div>
          <button
            onClick={() => {
              setHasSelectedPerformanceIndex(false);
              setPerformanceIndex(TRUE_COLOR_BASE_LAYER.id);
            }}
            className="button-select-new-field"
          >
            <HiOutlineChevronLeft />
            {t`Select new index`}
          </button>
          {PERFORMANCE_INDEX_LAYERS.filter((layer) => {
            return layer.id === performanceIndex;
          }).map(({ name, description, legendImage }) => (
            <PerformanceIndexDescription
              key={name()}
              title={name()}
              text={description.text()}
              values={description.values}
              legendImage={legendImage}
            />
          ))}
          <div className="observation-card-section">
            <div className="observation-card-list-title">{t`Available dates`}</div>
            <div className="observation-card-list">
              {availableDatesStatus === FETCH_STATUSES.error ? (
                <ErrorHandler
                  title={t`Error`}
                  description={t`Something went wrong when fetching available dates`}
                  onTryAgainClick={fetchCloudFreeDates}
                />
              ) : (
                availableDates.map((date) => (
                  <ObservationCard key={date.fromTime}>
                    <ObservationComponent
                      field={field}
                      date={date.fromTime}
                      height={200}
                      performanceIndex={performanceIndex}
                      language={language}
                    />
                    <div className="observation-card-information">
                      <div className="observation-card-information-title">
                        {dayjs(date.fromTime).format('YYYY-MM-DD')}
                      </div>
                      <ButtonPrimarySmall onClick={() => setDate(date.fromTime)}>
                        {t`Select date`}
                      </ButtonPrimarySmall>
                    </div>
                  </ObservationCard>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderSignalChart() {
    if (!isAnalysingMultipleFields) {
      return (
        <SignalChartComponent
          performanceIndex={performanceIndex}
          neighbouringFields={neighbouringFields}
          scope={FIELD_SCOPE}
          selectedField={field}
          onDateClick={(date) => setDate(date)}
          language={language}
          date={date}
        />
      );
    }

    if (neighbouringFieldsStatus === FETCH_STATUSES.loading) {
      return (
        <div className="chart-loader">
          <img src={Spinner} alt="" />
        </div>
      );
    }

    if (neighbouringFieldsStatus === FETCH_STATUSES.error) {
      return (
        <ErrorHandler
          title="Error"
          description="There was an error when fetching neighbouring fields"
          onTryAgainClick={getNeighbouringFields}
        />
      );
    }

    return (
      <SignalChartComponent
        performanceIndex={performanceIndex}
        neighbouringFields={neighbouringFields}
        scope={FIELD_SCOPE}
        selectedField={field}
        onDateClick={(date) => setDate(date)}
        language={language}
        date={date}
      />
    );
  }

  function renderFilters() {
    return (
      <div className="field-filters">
        <Filter
          popupPosition="top"
          popupDirection="right"
          onOutsideClick={() => handleFilterCloseClick(FILTERS.cropType)}
          onSaveChangesClick={() => handleFilterSaveClick(FILTERS.cropType)}
          onToggleClick={() => handleFilterToggleClick(FILTERS.cropType)}
          onCloseClick={() => handleFilterCloseClick(FILTERS.cropType)}
          isOpen={activeFilter === FILTERS.cropType}
          label={t`Crop type`}
        >
          {cropTypes.map((crop) => (
            <RadioButton
              key={crop.code}
              onChange={(event) => {
                handleFilterChange(FILTERS.cropType, event.currentTarget.value);
              }}
              value={crop.code}
              checked={unsavedFilters.cropType === crop.code}
              label={crop.names.de.toLowerCase()}
            />
          ))}
        </Filter>
        <Filter
          popupPosition="top"
          popupDirection="right"
          onOutsideClick={() => handleFilterCloseClick(FILTERS.soilType)}
          onSaveChangesClick={() => handleFilterSaveClick(FILTERS.soilType)}
          onToggleClick={() => handleFilterToggleClick(FILTERS.soilType)}
          onCloseClick={() => handleFilterCloseClick(FILTERS.soilType)}
          isOpen={activeFilter === FILTERS.soilType}
          label={t`Soil type`}
        >
          {soilTypes.map((soil) => (
            <RadioButton
              key={soil.code}
              onChange={(event) => {
                handleFilterChange(FILTERS.soilType, event.currentTarget.value);
              }}
              value={soil.code}
              checked={unsavedFilters.soilType === soil.code}
              label={soil.names.de.toLowerCase()}
            />
          ))}
        </Filter>
      </div>
    );
  }

  return (
    <FieldWrap>
      <PanelLeft>
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div className="field-information-stats">
            <div className="field-information">
              <FieldLabel icon={<FaTractor />} label={t`Crop type`} value={getCropNameById(field.cropId)} />
              <FieldLabel icon={<AiFillCalendar />} value={dayjs(date).format('YYYY-MM-DD')} label="Date" />
            </div>
          </div>
          {hasSelectedPerformanceIndex ? renderAnalysePerformanceIndex() : renderSelectPerformanceIndex()}
        </div>
      </PanelLeft>
      <FieldPanelRight>
        <div className="field-panel-top-right">
          <FieldMap
            date={date}
            activeLayer={performanceIndex}
            isAnalysingMultipleFields={isAnalysingMultipleFields}
            field={field}
            neighbouringFields={neighbouringFields}
            onFieldClick={(newFields) => {
              setNeighbouringFields(newFields);
            }}
          >
            {hasSelectedPerformanceIndex && (
              <div className="map-bottom-panel">
                <ButtonWhiteMedium
                  className="button-white-medium"
                  onClick={() => setShouldDisplayTimelapse(true)}
                >
                  {t`Play field timelapse`}
                  <BsFillPlayFill className="button-white-medium-icon" />
                </ButtonWhiteMedium>
              </div>
            )}
            {shouldDisplayTimelapse && hasSelectedPerformanceIndex && (
              <FieldTimelapse>
                <div className="map-bottom-panel map-bottom-panel-higher">
                  <ButtonWhiteMedium
                    className="button-white-medium"
                    onClick={() => setShouldDisplayTimelapse(false)}
                  >
                    {t`Close timelapse`}
                    <MdClose className="button-white-medium-icon" />
                  </ButtonWhiteMedium>
                </div>
                <TimelapseComponent
                  field={field}
                  date={date}
                  scope={FIELD_SCOPE}
                  performanceIndex={performanceIndex}
                  language={language}
                />
              </FieldTimelapse>
            )}
          </FieldMap>
        </div>
        {hasSelectedPerformanceIndex && (
          <div className="field-panel-bottom-right">
            <Switch
              className="field-analysation-togggle"
              label={t`Compare to similar fields`}
              onChange={(event) => setIsAnalysingMultipleFields(event.currentTarget.checked)}
              checked={isAnalysingMultipleFields}
            />
            {isAnalysingMultipleFields && renderFilters()}
            <div className="field-panel-chart">{renderSignalChart()}</div>
          </div>
        )}
      </FieldPanelRight>
    </FieldWrap>
  );
}
